/**
 * Shared metadata, glyphs and helpers for the Status Alerts admin surfaces
 * (Implementation Spec v1.2). Used by the Triage board, the Timeline view and
 * the composer. The taxonomy here mirrors backend/app/services/announcements.py
 * (ALERT_TYPES / LIFECYCLE_STATES / SERVICE_STATUSES / REMINDER_LEADS) one-for-one.
 *
 * Glyphs are multi-element SVGs lifted verbatim from the design handoff
 * (status-alerts/sa-data.js); the shared Icon component is single-path only, so
 * these live here. The path strings are static literals, never user input.
 */
import type { AlertType, AnnouncementAdmin } from "../services/api";

// --- glyphs -------------------------------------------------------------- //
const ICON_PATHS: Record<string, string> = {
  outage:
    '<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
  maintenance:
    '<path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.4-2.4 2.6-2.6z"/>',
  enhancement:
    '<path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z"/><path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15z"/>',
  globe:
    '<circle cx="12" cy="12" r="9"/><line x1="3" y1="12" x2="21" y2="12"/><path d="M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18z"/>',
  building:
    '<rect x="4" y="3" width="16" height="18" rx="1.5"/><line x1="9" y1="8" x2="9" y2="8.01"/><line x1="15" y1="8" x2="15" y2="8.01"/><line x1="9" y1="12" x2="9" y2="12.01"/><line x1="15" y1="12" x2="15" y2="12.01"/><path d="M10 21v-4h4v4"/>',
  users:
    '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>',
  mail:
    '<rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 7L2 7"/>',
  activity: '<path d="M22 12h-4l-3 9L9 3l-3 9H2"/>',
  clock: '<circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/>',
  bell:
    '<path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>',
  plus: '<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>',
  search: '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>',
  server:
    '<rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>',
  eye:
    '<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/>',
  arrowR:
    '<line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>',
  check: '<polyline points="20 6 9 17 4 12"/>',
  refresh:
    '<polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>',
  trash:
    '<polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>',
  list:
    '<line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>',
  bold:
    '<path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/>',
  listOrdered:
    '<line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/><path d="M4 6h1v4"/><path d="M4 10h2"/><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"/>',
  link:
    '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>',
};

export type SAIconName = keyof typeof ICON_PATHS;

export function SAIcon({
  name,
  strokeWidth = 2,
  style,
}: {
  name: SAIconName;
  strokeWidth?: number;
  style?: React.CSSProperties;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      aria-hidden
      // eslint-disable-next-line react/no-danger -- static literal glyph paths
      dangerouslySetInnerHTML={{ __html: ICON_PATHS[name] }}
    />
  );
}

// --- taxonomy ------------------------------------------------------------ //
export const TYPE_META: Record<
  AlertType,
  { label: string; tone: string; glyph: SAIconName; refPrefix: string; desc: string }
> = {
  outage: {
    label: "Outage",
    tone: "#C4262E",
    glyph: "outage",
    refPrefix: "INC",
    desc: "Something is broken or degraded right now.",
  },
  maintenance: {
    label: "Maintenance",
    tone: "#2A6FDB",
    glyph: "maintenance",
    refPrefix: "CHG",
    desc: "Planned work with expected downtime.",
  },
  enhancement: {
    label: "Enhancement",
    tone: "#5B3FE0",
    glyph: "enhancement",
    refPrefix: "ENH",
    desc: "A new capability or improvement is rolling out.",
  },
};

/** Ordered lifecycle [stateKey, label] per type. */
export const LIFECYCLE_STATES: Record<AlertType, [string, string][]> = {
  outage: [
    ["investigating", "Investigating"],
    ["monitoring", "Monitoring"],
    ["resolved", "Resolved"],
  ],
  maintenance: [
    ["scheduled", "Scheduled"],
    ["reminder", "Reminder sent"],
    ["complete", "Complete"],
  ],
  enhancement: [
    ["announced", "Announced"],
    ["reminder", "Reminder sent"],
    ["live", "Now live"],
  ],
};

export const STATE_LABEL: Record<string, string> = {
  draft: "Draft",
  investigating: "Investigating",
  monitoring: "Monitoring",
  resolved: "Resolved",
  scheduled: "Scheduled",
  reminder: "Reminder sent",
  complete: "Complete",
  announced: "Announced",
  live: "Now live",
};

/** Node colour for chips / lifecycle / timeline. */
export const STATE_TONE: Record<string, string> = {
  draft: "#6A6A72",
  investigating: "#C4262E",
  monitoring: "#B07800",
  reminder: "#B07800",
  scheduled: "#2A6FDB",
  announced: "#5B3FE0",
  resolved: "#0A8E3E",
  live: "#0A8E3E",
  complete: "#0A8E3E",
};

const TERMINAL_STATES = new Set(["resolved", "complete", "live"]);

/** Per-type allowed service statuses [statusKey, label]. */
export const SERVICE_STATUS: Record<AlertType, [string, string][]> = {
  outage: [
    ["down", "Down"],
    ["degraded", "Degraded"],
    ["recovering", "Recovering"],
    ["ok", "Operational"],
  ],
  maintenance: [
    // 'scheduled' is the stored key; customers read the plainer "Unavailable"
    // (Ryan, 2026-08-28). Distinct from the LIFECYCLE_STATES entry of the same
    // key, which still reads "Scheduled" because it names the lifecycle stage.
    ["scheduled", "Unavailable"],
    ["noimpact", "No impact"],
  ],
  enhancement: [
    ["new", "New"],
    ["improved", "Improved"],
    ["available", "Available"],
  ],
};

export const SERVICE_STATUS_LABEL: Record<string, string> = Object.fromEntries(
  Object.values(SERVICE_STATUS).flat(),
);

/** status -> email pill colour class suffix (.sa-ep-pill.<x>). */
export const SERVICE_PILL: Record<string, string> = {
  down: "red",
  degraded: "amber",
  recovering: "amber",
  ok: "green",
  scheduled: "blue",
  noimpact: "grey",
  new: "violet",
  improved: "violet",
  available: "green",
};

/** Reminder lead-time options [value, label] per type (none for outage). */
export const REMINDER_LEADS: Record<AlertType, [string, string][]> = {
  outage: [],
  maintenance: [
    ["morning", "The morning of the window (08:00)"],
    ["24h", "24 hours before"],
    ["2h", "2 hours before"],
  ],
  enhancement: [
    ["day_before", "The day before go-live (09:00)"],
    ["morning", "On go-live morning (08:00)"],
    ["week", "1 week before (09:00)"],
  ],
};

/** Legacy plain announcement (no alert_type) maps from severity. */
const SEVERITY_TYPE: Record<string, AlertType> = {
  incident: "outage",
  maintenance: "maintenance",
  info: "enhancement",
};

export function typeOf(a: AnnouncementAdmin): AlertType {
  return a.alert_type ?? SEVERITY_TYPE[a.severity] ?? "enhancement";
}

export function isDraft(a: AnnouncementAdmin): boolean {
  // A true Status Alert draft: saved, never published (no updates posted).
  return !a.is_active && a.alert_type != null && a.update_count === 0;
}

export function stateOf(a: AnnouncementAdmin): string {
  if (a.lifecycle_state) return a.lifecycle_state;
  if (isDraft(a)) return "draft";
  const t = typeOf(a);
  if (!a.is_active) return LIFECYCLE_STATES[t][LIFECYCLE_STATES[t].length - 1][0];
  return LIFECYCLE_STATES[t][0][0];
}

export function stateLabel(state: string): string {
  return STATE_LABEL[state] ?? state;
}

export type TriageColumn = "drafts" | "active" | "scheduled" | "resolved";

export function triageColumn(a: AnnouncementAdmin): TriageColumn {
  if (!a.is_active) {
    return isDraft(a) ? "drafts" : "resolved";
  }
  return typeOf(a) === "outage" ? "active" : "scheduled";
}

export function isTerminalState(state: string): boolean {
  return TERMINAL_STATES.has(state);
}

/** Email tone (data-tone): a closed/terminal alert renders green; otherwise the
 * type's own tone (outage red, maintenance blue, enhancement violet). */
export function emailTone(type: AlertType, state: string): string {
  if (isTerminalState(state)) return "green";
  return { outage: "red", maintenance: "blue", enhancement: "violet" }[type];
}

/** AWST display formatting (Brief hard rule: AWST display, UTC at rest). */
export function fmtAwst(iso: string | null | undefined): string {
  if (!iso) return "Not set";
  return new Date(iso).toLocaleString("en-AU", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Australia/Perth",
  });
}

export function fmtAwstShort(iso: string | null | undefined): string {
  if (!iso) return "Not set";
  return new Date(iso).toLocaleString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Australia/Perth",
  });
}

/**
 * The maintenance window / go-live line, in AWST, as customers read it.
 *
 * Shared by the customer banner, the composer's banner preview and its email
 * preview so all three say the same thing as the email the backend actually
 * sends (_sa_window_html in backend/app/integrations/smtp2go/templates.py).
 * Outages carry no window; returns null when there is nothing to state.
 */
export function windowParts(iso: string | null | undefined): {
  date: string;
  time: string;
} | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return {
    // en-AU renders this as "Fri, 28 Aug 2026"; drop the weekday comma so the
    // banner matches the email byte-for-byte (_fmt_awst on the backend).
    date: d
      .toLocaleDateString("en-AU", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "Australia/Perth",
      })
      .replace(",", ""),
    time: d.toLocaleTimeString("en-AU", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "Australia/Perth",
    }),
  };
}

/** '20 minutes' / '1 hour 30 minutes' between two instants, or null. */
export function windowDuration(
  start: string | null | undefined,
  end: string | null | undefined,
): string | null {
  if (!start || !end) return null;
  const mins = Math.floor(
    (new Date(end).getTime() - new Date(start).getTime()) / 60_000,
  );
  if (!Number.isFinite(mins) || mins <= 0) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const parts: string[] = [];
  if (h) parts.push(`${h} hour${h === 1 ? "" : "s"}`);
  if (m) parts.push(`${m} minute${m === 1 ? "" : "s"}`);
  return parts.join(" ");
}

export function windowLabel(
  alertType: AlertType | null | undefined,
  start: string | null | undefined,
  end: string | null | undefined,
): { label: string; value: string } | null {
  if (alertType === "enhancement") {
    const s = windowParts(start);
    return s ? { label: "Goes live", value: `${s.date}, ${s.time} AWST` } : null;
  }
  if (alertType !== "maintenance") return null;

  const s = windowParts(start);
  const e = windowParts(end);
  if (!s && !e) return null;

  let value: string;
  if (s && e) {
    // Same-day windows read better without the date repeated on the end.
    value =
      s.date === e.date
        ? `${s.date}, ${s.time} to ${e.time} AWST`
        : `${s.date}, ${s.time} to ${e.date}, ${e.time} AWST`;
  } else if (s) {
    value = `starts ${s.date}, ${s.time} AWST`;
  } else {
    value = `ends ${e!.date}, ${e!.time} AWST`;
  }
  const dur = windowDuration(start, end);
  return {
    label: "Maintenance window",
    value: dur ? `${value} (${dur})` : value,
  };
}

export function scopeLabel(a: AnnouncementAdmin): string {
  if (a.target_all) return "All clients";
  const n = a.target_company_ids?.length ?? 0;
  return `${n} client${n === 1 ? "" : "s"}`;
}
